// As rotas.
//
// Tudo atrás do login está dentro de <Protegido>: uma rota nova não vaza por
// esquecimento de embrulhar. Isso é DESENHO, não segurança — quem chamasse a
// API direto levaria 401 do servidor de qualquer forma.

import { Navigate, Route, BrowserRouter, Routes, useOutletContext } from "react-router-dom";
import type { ReactNode } from "react";

import { Entrar } from "./Entrar";
import { Conta } from "./conta/Conta";
import { PainelLayout } from "./painel/Layout";
import { VisaoGeral } from "./painel/VisaoGeral";
import { Bloqueios } from "./painel/Bloqueios";
import { Dominios } from "./painel/Dominios";
import { Seguranca } from "./painel/Seguranca";
import { Auditoria, Caixas, Entregas, Quarentena, Revendas } from "./painel/Listas";
import { Backups, Licenca, Relay } from "./painel/Previstas";
import { Integracoes } from "./painel/Integracoes";
import { Migracao } from "./painel/Migracao";
import { MarcaRevenda } from "./painel/MarcaRevenda";
import { AplicarMarca } from "./ui/Marca";
import { Busca } from "./correio/Busca";
import { Contatos } from "./correio/Contatos";
import { NaoRecebi } from "./correio/NaoRecebi";
import { MeuEmailChegou } from "./correio/MeuEmailChegou";
import { Layout } from "./correio/Layout";
import { Leitura, type ModoDeResposta } from "./correio/Leitura";
import type { Mensagem } from "./api/tipos";
import { Lista } from "./correio/Lista";
import { Programados } from "./correio/Programados";
import { ProvedorDeSessao, useSessao } from "./sessao/contexto";

interface ContextoDaTela {
  responder: (modo: ModoDeResposta, mensagem: Mensagem) => void;
  /** Abre a janela de escrita já preenchida. Ver correio/Layout. */
  escreverCom: (campos: {
    para?: string;
    cc?: string;
    assunto?: string;
    corpo?: string;
  }) => void;
}

/** Dá à tela de leitura o jeito de abrir a janela de escrita do Layout. */
function LeituraLigada() {
  const { responder } = useOutletContext<ContextoDaTela>();
  return <Leitura aoResponder={responder} />;
}

/** O mesmo, para a tela de Programados: cancelar devolve o texto. */
function ProgramadosLigado() {
  const { escreverCom } = useOutletContext<ContextoDaTela>();
  return <Programados aoReabrir={escreverCom} />;
}

/**
 * Só para quem tem o que administrar.
 *
 * O alternador já não aparece para usuário comum, mas digitar /painel
 * funcionava: ele via a casca do painel com metade dos números dando 403 —
 * feio, e confuso o bastante para virar chamado. Isso é DESENHO, não
 * segurança: quem forjasse o nível veria as telas vazias, porque quem filtra
 * é a API.
 */
function SoAdministradores({ children }: { children: ReactNode }) {
  const { podeVer, carregando } = useSessao();
  if (carregando) return <div className="min-h-screen bg-fundo" />;
  if (!podeVer("admin_dominio")) return <Navigate to="/correio/INBOX" replace />;
  return <>{children}</>;
}

function Protegido({ children }: { children: ReactNode }) {
  const { sessao, carregando, definir, adicionando, cancelarAdicao } = useSessao();
  // Tela em branco enquanto o cookie é trocado por um token. Mostrar o login
  // aqui faria a tela piscar "entre de novo" a cada F5 de quem está logado.
  if (carregando) return <div className="min-h-screen bg-fundo" />;
  // ADICIONAR CONTA mostra o login SEM derrubar a atual: a nova entra numa
  // vaga própria, e as outras abas continuam onde estavam. O botão de voltar
  // existe porque sem ele a única saída seria entrar em alguma conta.
  if (adicionando) {
    return <Entrar aoEntrar={definir} aoDesistir={sessao ? cancelarAdicao : undefined} />;
  }
  if (!sessao) return <Entrar aoEntrar={definir} />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <ProvedorDeSessao>
        {/* Título e ícone da aba com a marca de quem está vendo — da
            plataforma ou da revenda. Ver ui/Marca.tsx. */}
        <AplicarMarca />
        <Routes>
          <Route
            path="/"
            element={
              <Protegido>
                <Layout />
              </Protegido>
            }
          >
            <Route index element={<Navigate to="/correio/INBOX" replace />} />
            <Route path="correio/:pasta" element={<Lista />} />
            <Route path="correio/:pasta/:uuid" element={<LeituraLigada />} />
            <Route path="busca" element={<Busca />} />
            <Route path="contatos" element={<Contatos />} />
            {/* PROGRAMADOS não é pasta IMAP, e por isso tem rota própria em
                vez de entrar em /correio/:pasta: a mensagem ainda não está
                no Dovecot — vive em `cdw.envios_agendados` e só se torna
                mensagem na hora de sair. Ver correio/Programados.tsx. */}
            <Route path="programados" element={<ProgramadosLigado />} />
            {/* O DIAGNÓSTICO, para quem USA a caixa.
                
                Fora de /painel de propósito: a resposta a "não recebi" e
                "meu e-mail chegou?" interessa a QUALQUER pessoa com uma
                caixa, não só a quem administra. A API já permitia — as duas
                rotas aceitam `sessao.Usuario` e se limitam sozinhas à caixa
                de quem pergunta —; o que faltava era a tela. */}
            <Route path="nao-recebi" element={<NaoRecebi />} />
            <Route path="meu-email-chegou" element={<MeuEmailChegou />} />
            <Route path="conta" element={<Conta />} />
            {/* Caminho desconhecido volta para a caixa de entrada em vez de
                mostrar uma tela vazia sem saída. */}
            <Route path="*" element={<Navigate to="/correio/INBOX" replace />} />
          </Route>

          {/* O PAINEL é o outro modo do MESMO aplicativo: mesma sessão, mesmo
              cliente de API, sem recarregar a página ao trocar de área.
              Layout próprio porque a barra lateral é outra. */}
          <Route
            path="/painel"
            element={
              <Protegido>
                <SoAdministradores>
                  <PainelLayout />
                </SoAdministradores>
              </Protegido>
            }
          >
            <Route index element={<VisaoGeral />} />
            <Route path="revendas" element={<Revendas />} />
            <Route path="dominios" element={<Dominios />} />
            <Route path="caixas" element={<Caixas />} />
            <Route path="entregas" element={<Entregas />} />
            <Route path="quarentena" element={<Quarentena />} />
            <Route path="bloqueios" element={<Bloqueios />} />
            <Route path="seguranca-ip" element={<Seguranca />} />
            <Route path="marca" element={<MarcaRevenda />} />
            <Route path="auditoria" element={<Auditoria />} />
            <Route path="relay" element={<Relay />} />
            <Route path="migracao" element={<Migracao />} />

            <Route path="backups" element={<Backups />} />
            <Route path="licenca" element={<Licenca />} />
            <Route path="integracoes" element={<Integracoes />} />
          </Route>
        </Routes>
      </ProvedorDeSessao>
    </BrowserRouter>
  );
}
